import * as React from 'react';

import { LucideProps } from 'lucide-react';
import {
  Button as AriaButton,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  PopoverProps as AriaPopoverProps,
  MenuTrigger,
  Separator,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { Popover } from '../overlays/Popover';

export type SecondStepProps = {
  close: () => void;
};

export type MenuItem =
  | 'separator'
  | {
      label: React.ReactNode;
      onAction?: () => void;
      href?: string;
      isDisabled?: boolean;
      icon?: React.ReactElement<LucideProps & { className?: string }>;
      className?: string;
      secondStep?: (props: SecondStepProps) => React.ReactNode;
    };

export type MenuProps = {
  trigger:
    | string
    | React.ReactElement<{
        onClick: () => void;
        ref: React.RefObject<HTMLButtonElement | null>;
      }>;
  triggerClassName?: string;
  placement?: AriaPopoverProps['placement'];
  menuClassName?: string;
  highlightOnOpen?: boolean;
  items: MenuItem[];
  size?: 'default' | 'xl';
  onOpenChange?: (open: boolean) => void;
};

type MenuTriggerContentProps = {
  trigger: MenuProps['trigger'];
  highlightOnOpen: boolean;
  isOpen: boolean;
};

//
// ⚛️ ⚛️ ⚛️ MENU TRIGGER CONTENT ⚛️ ⚛️ ⚛️
//
const MenuTriggerContent = ({
  trigger,
  highlightOnOpen,
  isOpen,
}: MenuTriggerContentProps) => {
  if (highlightOnOpen) {
    return <div data-menu-open={isOpen || undefined}>{trigger}</div>;
  }
  return trigger;
};

//
// ⚛️ ⚛️ ⚛️ MENU ⚛️ ⚛️ ⚛️
//
export const Menu = ({
  highlightOnOpen = false,
  size = 'default',
  ...props
}: MenuProps) => {
  // Refs
  const preventClosingRef = React.useRef(false);
  const secondStepRef =
    React.useRef<(props: SecondStepProps) => React.ReactNode | null>(null);

  // States
  const [isOpen, setIsOpen] = React.useState(false);
  const [showSecondStep, setShowSecondStep] = React.useState(false);

  // Memoized values
  const hasIcon = React.useMemo(
    () => props.items.some((item) => item !== 'separator' && item.icon),
    [props.items],
  );

  const iconSize = size === 'xl' ? 20 : 16;

  // Callbacks
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (preventClosingRef.current && !open) {
        preventClosingRef.current = false;
        return;
      }
      setIsOpen(open);
      props.onOpenChange?.(open);
    },
    [props.onOpenChange],
  );

  /** Reset second step state when menu closes */
  React.useEffect(() => {
    if (!isOpen) {
      setShowSecondStep(false);
      preventClosingRef.current = false;
      secondStepRef.current = null;
    }
  }, [isOpen]);

  return (
    <MenuTrigger onOpenChange={handleOpenChange} isOpen={isOpen}>
      {typeof props.trigger === 'string' ? (
        <AriaButton
          className={cn('@MenuTriggerButton', props.triggerClassName)}
          onClick={() => setIsOpen(!isOpen)}
        >
          {props.trigger}
        </AriaButton>
      ) : (
        <MenuTriggerContent
          trigger={props.trigger}
          highlightOnOpen={highlightOnOpen}
          isOpen={isOpen}
        />
      )}
      <Popover
        className={'overflow-clip p-[2px]'}
        placement={props.placement ?? 'bottom right'}
      >
        {showSecondStep ? (
          <div className="flex flex-col p-2">
            {secondStepRef.current?.({ close: () => setIsOpen(false) })}
          </div>
        ) : (
          <AriaMenu className={cn('outline-none', props.menuClassName)}>
            {props.items.map((item, index) =>
              item === 'separator' ? (
                <Separator
                  key={index}
                  className={cn(
                    'border-t border-neutral/10',
                    size === 'xl' && 'my-1',
                    size !== 'xl' && 'my-0.5',
                  )}
                />
              ) : (
                <AriaMenuItem
                  className={() =>
                    cn(
                      'flex cursor-pointer items-center gap-2 truncate rounded',
                      'focus:bg-current/8 focus:outline-none',
                      !item.isDisabled && 'hover:bg-current/8',
                      item.isDisabled && 'cursor-not-allowed opacity-50',
                      size === 'xl' && 'px-4 py-3.5 text-base',
                      size !== 'xl' && 'px-3 py-2 text-sm',
                      item.className,
                    )
                  }
                  key={index}
                  onAction={() => {
                    if (item.secondStep) {
                      preventClosingRef.current = true;
                      secondStepRef.current = item.secondStep;
                      setShowSecondStep(true);
                    } else {
                      item.onAction?.();
                    }
                  }}
                  href={item.href}
                  isDisabled={item.isDisabled}
                >
                  {!item.icon && hasIcon && (
                    <div
                      className={cn(
                        size === 'xl' && 'size-5',
                        size !== 'xl' && 'size-4',
                      )}
                    />
                  )}
                  {item.icon &&
                    React.cloneElement(item.icon, {
                      size: iconSize,
                      className: 'opacity-40',
                    })}
                  {item.label}
                </AriaMenuItem>
              ),
            )}
          </AriaMenu>
        )}
      </Popover>
    </MenuTrigger>
  );
};
